import React, { useEffect, useState } from "react";
import axios from "axios";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from "chart.js";
import { CircularProgress, Container, Typography } from "@mui/material";
import config from "../config.js";

// Register necessary Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

function RadarChartDisplay() {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const response = await axios.get(`${API_BASE_URL}/api/v1/workbookToJson`);
        
        
        // Check if response.data and response.data.data exist before trying to access Sheet1
        if (!response.data || !response.data.data || !response.data.data.Sheet1) {
          throw new Error("Invalid data format received from API");
        }
        
        const rawData = response.data.data.Sheet1;
        
        // Filter data - make sure we have items with the right properties
        const filteredData = rawData.filter((item, index) => 
          index !== 0 && 
          item["Personal Budget"] !== "Total" && 
          typeof item["__EMPTY"] === "number"
        );
        
        // Extract labels and data
        const labels = filteredData.map(item => item["__EMPTY_1"] || "Unnamed");
        const data = filteredData.map(item => item["__EMPTY"] || 0);
        
        // Calculate max value for scale (round up to nearest hundred)
        const maxValue = Math.ceil(Math.max(...data) / 100) * 100;
        
        setChartData({ 
          labels, 
          data,
          maxValue 
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading Chart
        </Typography>
        <Typography>
          {error.message || "Failed to load chart data"}
        </Typography>
      </Container>
    );
  }

  if (loading || !chartData) {
    return (
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading chart data...</Typography>
      </Container>
    );
  }

  // Setup the chart data
  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Budget Expenses ($)",
        data: chartData.data,
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 2,
        pointBackgroundColor: "rgba(54, 162, 235, 1)",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(54, 162, 235, 1)",
        pointRadius: 4,
      },
    ],
  };

  // Chart options
  const options = {
    scales: {
      r: {
        angleLines: {
          display: true,
          color: "rgba(0, 0, 0, 0.1)",
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        suggestedMin: 0,
        suggestedMax: chartData.maxValue,
        ticks: {
          stepSize: Math.ceil(chartData.maxValue / 5),
          callback: function(value) {
            return '$' + value;
          }
        },
        pointLabels: {
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: $${context.raw}`;
          }
        }
      }
    },
    maintainAspectRatio: false,
  };

  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom sx={{ my: 2 }}>
        Budget Expenses Comparison
      </Typography>
      
      <div
        style={{
          padding: "20px",
          width: "100%",
          height: "500px", // Fixed height for better visibility
        }}
      >
        <Radar data={data} options={options} />
      </div>
    </Container>
  );
}

export default RadarChartDisplay;


